import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Award, Lock, Star, Trophy, Zap, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BadgesDisplay = ({ token }) => {
  const { t } = useTranslation();
  const [badges, setBadges] = useState([]);
  const [stats, setStats] = useState({ total_earned: 0, total_available: 0 });
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBadges();
  }, []);

  const fetchBadges = async () => {
    try {
      const response = await axios.get(`${API}/gamification/badges`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setBadges(response.data.badges || []);
      setStats({
        total_earned: response.data.total_earned || 0,
        total_available: response.data.total_available || 0
      });
    } catch (error) {
      console.error('Error fetching badges:', error);
      // Set default badges if API fails
      setBadges([
        { id: 'first_meal', name: 'First Step', description: 'Log your first meal', icon: '🎯', earned: false },
        { id: 'streak_7', name: 'Week Warrior', description: '7-day logging streak', icon: '🔥', earned: false },
        { id: 'meals_10', name: 'Getting Started', description: 'Log 10 meals', icon: '🌟', earned: false },
        { id: 'meals_50', name: 'Consistent Tracker', description: 'Log 50 meals', icon: '⭐', earned: false },
        { id: 'meals_100', name: 'Nutrition Expert', description: 'Log 100 meals', icon: '🏆', earned: false },
        { id: 'goal_reached', name: 'Goal Achieved', description: 'Reach your weight goal', icon: '🎉', earned: false },
      ]);
      setStats({ total_earned: 0, total_available: 6 });
    } finally {
      setLoading(false);
    }
  };

  const getBadgeDetails = (badge) => {
    const details = {
      first_meal: { 
        requirement: 'Log your first meal',
        progress: 'Take a photo of your food to get started!',
        color: 'text-blue-500'
      },
      streak_7: { 
        requirement: 'Log meals for 7 days in a row',
        progress: 'Keep logging daily to build your streak!',
        color: 'text-orange-500'
      },
      streak_30: { 
        requirement: 'Log meals for 30 days in a row',
        progress: 'Master consistency for a full month!',
        color: 'text-red-500'
      },
      streak_100: { 
        requirement: 'Log meals for 100 days in a row',
        progress: 'Ultimate dedication challenge!',
        color: 'text-purple-500'
      },
      meals_10: { 
        requirement: 'Log 10 total meals',
        progress: 'Keep logging to reach this milestone!',
        color: 'text-green-500'
      },
      meals_50: { 
        requirement: 'Log 50 total meals',
        progress: 'You are becoming a consistent tracker!',
        color: 'text-blue-600'
      },
      meals_100: { 
        requirement: 'Log 100 total meals',
        progress: 'Show your nutrition expertise!',
        color: 'text-yellow-600'
      },
      meals_500: { 
        requirement: 'Log 500 total meals',
        progress: 'Dedication at its finest!',
        color: 'text-indigo-600'
      },
      goal_reached: { 
        requirement: 'Reach your target weight',
        progress: 'Stay consistent to achieve your goal!',
        color: 'text-pink-500'
      },
      water_goal_7: { 
        requirement: 'Meet daily water goal for 7 days',
        progress: 'Stay hydrated every day!',
        color: 'text-cyan-500'
      }
    };
    
    return details[badge.id] || { requirement: badge.description, progress: 'Keep working towards this goal!', color: 'text-gray-500 dark:text-gray-400' };
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-lg">
        <div className="text-center py-8">
          <div className="animate-spin w-8 h-8 border-4 border-[#FF6600] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-500 dark:text-gray-400 text-sm">Loading achievements...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-lg">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold flex items-center gap-2">
          <Trophy className="text-[#FF6600]" size={24} />
          {t('badges.title')}
        </h2>
        <div className="text-center">
          <div className="text-lg font-bold text-[#FF6600]">
            {stats.total_earned} / {stats.total_available}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Badges Earned</div>
        </div>
      </div>

      {stats.total_earned === 0 && (
        <div className="mb-4 p-3 bg-orange-50 rounded-lg border-l-4 border-[#FF6600]">
          <div className="flex items-center gap-2 mb-2">
            <Star className="text-[#FF6600]" size={16} />
            <span className="text-sm font-semibold text-[#FF6600]">Get Started!</span>
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-300">
            Start logging your meals to unlock your first achievement! Each badge you earn shows your progress on your health journey.
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {badges.map((badge) => {
          const details = getBadgeDetails(badge);
          return (
            <div
              key={badge.id}
              onClick={() => setSelectedBadge(badge)}
              className={`p-3 rounded-lg border-2 text-center transition-all cursor-pointer hover:scale-105 ${
                badge.earned
                  ? 'border-[#FF6600] bg-orange-50 shadow-lg'
                  : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 opacity-75 hover:opacity-90'
              }`}
            >
              <div className="text-3xl mb-2">{badge.icon}</div>
              <div className="text-sm font-semibold mb-1">{badge.name}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">{badge.description}</div>
              
              {badge.earned ? (
                <div className="flex items-center justify-center gap-1 text-xs text-green-600 font-medium">
                  <CheckCircle size={12} />
                  Earned!
                </div>
              ) : (
                <div className="flex items-center justify-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                  <Lock size={12} />
                  Locked
                </div>
              )}
            </div>
          );
        })}
      </div>

      {stats.total_earned > 0 && (
        <div className="mt-4 text-center p-4 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 rounded-lg">
          <Zap className="text-[#FF6600] mx-auto mb-2" size={20} />
          <p className="text-sm font-semibold mb-1">Great Progress! 🎉</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            You've earned {stats.total_earned} achievement{stats.total_earned !== 1 ? 's' : ''}! Keep logging to unlock more.
          </p>
        </div>
      )}

      {/* Badge Detail Modal */}
      {selectedBadge && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" 
          onClick={() => setSelectedBadge(null)}
        >
          <div 
            className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-xl max-w-sm w-full mx-4 max-h-[85vh] overflow-y-auto relative" 
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setSelectedBadge(null)}
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 text-xl"
            >
              ×
            </button>
            
            <div className="text-center pt-2">
              <div className="text-5xl mb-3">{selectedBadge.icon}</div>
              <h3 className="text-xl font-bold mb-2">{selectedBadge.name}</h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">{selectedBadge.description}</p>
              
              {selectedBadge.earned ? (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
                  <CheckCircle className="text-green-600 mx-auto mb-2" size={20} />
                  <p className="font-semibold text-green-700 text-sm">Achievement Unlocked! 🎉</p>
                  <p className="text-green-600 text-xs mt-1">You've earned this badge!</p>
                </div>
              ) : (
                <div className="p-4 bg-orange-50 border border-[#FF6600] rounded-lg mb-4">
                  <Lock className="text-[#FF6600] mx-auto mb-2" size={20} />
                  <p className="font-semibold text-[#FF6600] text-sm mb-2">How to Unlock:</p>
                  <p className="text-gray-600 dark:text-gray-300 text-xs">{getBadgeDetails(selectedBadge).requirement}</p>
                  <p className="text-gray-500 dark:text-gray-400 text-xs mt-2">{getBadgeDetails(selectedBadge).progress}</p>
                </div>
              )}
              
              <button 
                onClick={() => setSelectedBadge(null)}
                className="w-full py-3 bg-[#FF6600] text-white font-semibold rounded-lg hover:bg-[#E55C00] transition-colors"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BadgesDisplay;